/**
 * Bake a three.js `AnimationClip` that drives some arbitrary source skeleton into a
 * `CanonicalAnimationClip` the library can hold.
 *
 * The clip is played on the source skeleton through `AnimationMixer.setTime` — deterministic, no
 * clock, no rAF, and the same technique the headless test surface already uses — and read back
 * one frame at a time through `BoundRig.readCanonicalPose()`. That is the whole trick: the rig
 * already knows how to translate this skeleton's local rotations into canonical ones, including
 * the rest alignment, so an imported clip lands in exactly the same space as a hand-authored one.
 *
 * Position tracks are PLAYED but not stored as such: they move the source skeleton so the Hips
 * world height can be read back per frame as the normalized `hipsY` channel (a crouch has to
 * survive the import, or the feet float). What survives into the canonical clip is rotations plus
 * that one scalar on the Hips keys. Horizontal travel is still dropped — root motion belongs to
 * the game engine, not the clip — and so is everything that is not a bone transform (scale tracks,
 * morph targets, cameras, lights).
 *
 * The source skeleton is left exactly as it was found: without that, sampling a second clip from
 * the same file would take the first clip's last frame as the rest pose.
 */

import { AnimationClip, AnimationMixer, LoopOnce, QuaternionKeyframeTrack } from 'three';
import type { KeyframeTrack, Object3D } from 'three';

import {
  AppError,
  CANONICAL_BONE_IDS,
  DEFAULT_EASING,
  DEFAULT_FPS,
  cloneQuat,
  newId,
} from '../shared';
import type {
  BoneRotationTrack,
  CanonicalAnimationClip,
  CanonicalBoneId,
  RotationKeyframe,
} from '../shared';
import { captureRestPose, restoreRestPose } from './sourceSkeleton';
import {
  DEFAULT_DECIMATION_DEGREES,
  DEFAULT_HIPS_Y_TOLERANCE,
  decimateKeyframes,
} from './keyDecimate';
import type { BoundRig } from '../runtime/boundRig';

/** Longer than this and the editor stops being usable; the caller decides whether to go on. */
export const MAX_SAMPLE_FRAMES = 3600;

/**
 * Below this, the sampled vertical hips motion is noise and the channel is not written at all.
 * 0.1 % of leg length is under a millimetre on a human-sized rig, far below anything a viewer can
 * see — and writing nothing is what keeps a rotation-only or XZ-only source importing to a
 * byte-for-byte rotation-only clip.
 */
export const HIPS_Y_IMPORT_THRESHOLD = 1e-3;

/**
 * Horizontal travel, as a fraction of leg length, above which the import warns about sliding feet.
 *
 * A genuine Mixamo "In Place" walk is not motionless: the pelvis sways 2–4 cm sideways over a
 * stride, which at 2 % of leg length (~1.7 cm) would have warned about every clean download. 25 %
 * of leg length is ~21 cm on a human — far beyond any sway, and far below the metre-plus a real
 * uncorrected walk or run covers (`fixtures/mixamo-wave.glb` travels 1.2 units ≈ 1.4 leg lengths).
 */
const HORIZONTAL_TRAVEL_WARNING = 0.25;

/**
 * How far before `duration` the final frame is sampled, in seconds.
 *
 * `frameCount = round(duration * fps)`, so for any clip whose length is a whole number of frames
 * — a 30-frame clip at 30 fps, i.e. most Mixamo downloads — the last sample lands on `t ===
 * duration` exactly. `AnimationAction._updateTime` treats that as the end of the cycle, so the
 * final keyframe of an imported wave or attack used to be its FIRST pose (audit §2.4 #1): the
 * animation snapped back to its start on the last frame and nothing said so. Backing off by a
 * microsecond keeps every sample strictly inside the clip. 1 µs is ~3·10⁻⁵ of a frame at 30 fps,
 * far below the 0.5° decimation tolerance, so it costs nothing measurable.
 */
const SAMPLE_END_EPSILON = 1e-6;

export interface SampleClipOptions {
  /** Name for the resulting library clip. */
  name: string;
  /** Frames per second to bake at. Rounded and clamped to the 1..240 the library accepts. */
  fps?: number;
  /** Angular tolerance for keyframe decimation, in degrees. 0 keeps every sampled frame. */
  toleranceDegrees?: number;
  /** Tolerance for the `hipsY` curve during decimation, in leg lengths. */
  hipsYTolerance?: number;
  loop?: boolean;
}

export interface SampleClipResult {
  clip: CanonicalAnimationClip;
  /** Keys before decimation, so the UI can report what it saved. */
  sampledKeyCount: number;
  keptKeyCount: number;
  warnings: string[];
  /**
   * Range of the sampled `hipsY`, present only when the channel was actually written onto the
   * Hips keys. Absent for a rotation-only or purely horizontal source.
   */
  hipsYRange?: { min: number; max: number };
}

/** A track name is `"<node>.<property>"`; morph tracks add `[name]`, which we never want. */
function trackProperty(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1);
}

/** True when the track rotates a node — the only kind a canonical clip can carry. */
function isRotationTrack(track: KeyframeTrack): boolean {
  return track instanceof QuaternionKeyframeTrack && trackProperty(track.name) === 'quaternion';
}

/** True when the clip moves a node, i.e. it carries root motion or other translation. */
export function hasTranslationTracks(clip: AnimationClip): boolean {
  return clip.tracks.some((t) => trackProperty(t.name) === 'position');
}

/**
 * Guess the clip's authoring rate from its keyframe spacing. glTF and FBX both store times in
 * seconds and neither records an fps, but a baked clip has an almost constant step — the median
 * is robust against the odd duplicated or missing key.
 */
export function inferFps(clip: AnimationClip): number | null {
  const deltas: number[] = [];
  for (const track of clip.tracks) {
    const times = track.times;
    for (let i = 1; i < times.length; i++) {
      const d = times[i] - times[i - 1];
      if (d > 1e-6) deltas.push(d);
    }
  }
  if (deltas.length === 0) return null;
  deltas.sort((a, b) => a - b);
  const median = deltas[Math.floor(deltas.length / 2)];
  const fps = Math.round(1 / median);
  return fps >= 1 && fps <= 240 ? fps : null;
}

/** The library only accepts an integer fps in 1..240 (`persistence/validate.ts`). */
export function normalizeFps(fps: number | null | undefined): number {
  if (fps == null || !Number.isFinite(fps)) return DEFAULT_FPS;
  return Math.min(240, Math.max(1, Math.round(fps)));
}

/** True when the track moves a node. Kept so the Hips world height is animated while sampling. */
function isPositionTrack(track: KeyframeTrack): boolean {
  return trackProperty(track.name) === 'position';
}

/**
 * A copy of `clip` carrying bone rotation AND position tracks. Never mutates the input.
 *
 * Positions are played, not stored: `readCanonicalLocal` reads only `.quaternion`, so a position
 * track cannot contaminate a rotation sample (vault `animation-import-pipeline` #1) — but it is
 * the only way the Hips actually reach their animated world height, which is what `hipsY` is
 * measured from. Scale and morph tracks are still dropped.
 */
function boneTransformsOnly(clip: AnimationClip): AnimationClip {
  const tracks = clip.tracks.filter((t) => isRotationTrack(t) || isPositionTrack(t));
  const copy = new AnimationClip(clip.name, -1, tracks);
  copy.resetDuration(); // FBXLoader hands out duration -1
  return copy;
}

/**
 * Sample `clip` onto `rig` and return it as a canonical clip.
 *
 * @throws AppError `ANIM_EMPTY_CLIP` when the clip rotates nothing this rig is bound to.
 */
export function sampleCanonicalClip(
  root: Object3D,
  rig: BoundRig,
  clip: AnimationClip,
  options: SampleClipOptions,
): SampleClipResult {
  const warnings: string[] = [];

  const playable = boneTransformsOnly(clip);
  if (!playable.tracks.some(isRotationTrack)) {
    throw new AppError(
      'ANIM_EMPTY_CLIP',
      `"${clip.name}" contains no bone rotation at all, so there is nothing to import from it.`,
      { clipName: clip.name },
    );
  }

  const fps = normalizeFps(options.fps ?? inferFps(clip));
  const duration = Math.max(0, playable.duration);
  const frameCount = Math.min(MAX_SAMPLE_FRAMES, Math.max(1, Math.round(duration * fps)));
  if (Math.round(duration * fps) > MAX_SAMPLE_FRAMES) {
    warnings.push(
      `The clip is ${duration.toFixed(1)}s long; only the first ${MAX_SAMPLE_FRAMES} frames ` +
        `(${(MAX_SAMPLE_FRAMES / fps).toFixed(1)}s) were imported.`,
    );
  }

  const mapped = rig.mappedBoneIds();
  const samples = new Map<CanonicalBoneId, RotationKeyframe[]>();
  for (const id of mapped) samples.set(id, []);

  // The vertical channel is only meaningful when the rig could measure a leg length at bind.
  const legLength = rig.legLength();
  const channelEnabled = legLength > 0 && rig.hipsLocalPosition(0) != null;
  const restHips = rig.restJointWorldPositions().Hips ?? [0, 0, 0];
  const up = rig.upWorld();
  /** Sampled `hipsY` indexed by frame, so a decimated key can still find its own value. */
  const hipsYByFrame: number[] = [];
  let maxHorizontal = 0;

  // The skeleton is a shared, live object: put it back exactly as found (D14), or the next clip
  // sampled from this same file would treat this clip's last frame as the rest pose. Position
  // tracks are played now, so the snapshot covers positions too (`captureRestPose`).
  const restPose = captureRestPose(root);
  const mixer = new AnimationMixer(root);
  try {
    const action = mixer.clipAction(playable);
    /*
     * Sampling is a scrub, not playback, so the action must never wrap.
     *
     * three's default is `LoopRepeat` with `repetitions = Infinity`; the source clip carries no
     * loop flag of its own, and an imported clip's own `loop` is an editor property applied at
     * playback, not at bake time. Both lines are required and neither is sufficient:
     *  - `LoopOnce` alone would make it WORSE — a finished `LoopOnce` action is disabled, its
     *    weight drops to 0 and the bone falls back to its REST pose, so the last frame would
     *    become rest instead of frame 0;
     *  - `clampWhenFinished` holds the final pose instead, but a paused action reports a
     *    time scale of 0 (`AnimationAction._updateTimeScale`), which would freeze every later
     *    `setTime`. Nothing here ever reaches `duration` (see `SAMPLE_END_EPSILON`), so the
     *    action never finishes and this is belt-and-braces against a future caller that does.
     */
    action.setLoop(LoopOnce, 1);
    action.clampWhenFinished = true;
    action.play();

    // Strictly inside the clip: `frameCount / fps` is exactly `duration` whenever the clip is a
    // whole number of frames long, which is where the wrap-to-frame-0 bug lived.
    const lastSampleTime = Math.max(0, duration - SAMPLE_END_EPSILON);

    for (let frame = 0; frame <= frameCount; frame++) {
      mixer.setTime(Math.min(frame / fps, lastSampleTime));
      root.updateMatrixWorld(true);
      const pose = rig.readCanonicalPose();
      for (const id of mapped) {
        const q = pose[id];
        if (!q) continue;
        samples.get(id)?.push({ frame, rotation: cloneQuat(q), easing: DEFAULT_EASING });
      }

      if (channelEnabled) {
        hipsYByFrame[frame] = rig.readHipsY();
        // Horizontal travel is the part of the Hips displacement that is NOT along model-up —
        // the part this import drops, and the only part worth warning about.
        const p = rig.worldPosition('Hips');
        const dx = p[0] - restHips[0];
        const dy = p[1] - restHips[1];
        const dz = p[2] - restHips[2];
        const along = dx * up[0] + dy * up[1] + dz * up[2];
        const hx = dx - along * up[0];
        const hy = dy - along * up[1];
        const hz = dz - along * up[2];
        const horizontal = Math.sqrt(hx * hx + hy * hy + hz * hz) / legLength;
        if (horizontal > maxHorizontal) maxHorizontal = horizontal;
      }
    }
  } finally {
    mixer.stopAllAction();
    mixer.uncacheClip(playable);
    restoreRestPose(root, restPose);
  }

  // Root motion is dropped, and dropping it gives sliding feet rather than an in-place walk — so
  // the warning has to name the fix, not the symptom. Vertical hips motion is no longer dropped,
  // so a crouch or a jump squat must NOT trigger it. When the rig could not measure a leg length
  // there is nothing to normalize against, so fall back to "the file has position tracks", which
  // is what this warning used to mean.
  const travelled = channelEnabled
    ? maxHorizontal > HORIZONTAL_TRAVEL_WARNING
    : hasTranslationTracks(clip);
  if (travelled) {
    warnings.push(
      'This clip travels horizontally; the movement was dropped (vertical hips motion is kept) ' +
        '— and the leg rotations were authored to go with it, so the feet will slide. ' +
        'Re-download the animation with the "In Place" option for a clean loop.',
    );
  }

  // Below the threshold the channel is not written at all: an XZ-only walk or a rotation-only
  // source must still produce a byte-for-byte rotation-only clip (clipFingerprint, GLB export).
  let hipsYRange: { min: number; max: number } | undefined;
  if (channelEnabled) {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    let peak = 0;
    for (const y of hipsYByFrame) {
      if (!Number.isFinite(y)) continue;
      if (y < min) min = y;
      if (y > max) max = y;
      peak = Math.max(peak, Math.abs(y));
    }
    if (peak >= HIPS_Y_IMPORT_THRESHOLD) {
      hipsYRange = { min, max };
      for (const key of samples.get('Hips') ?? []) {
        const y = hipsYByFrame[key.frame];
        if (Number.isFinite(y)) key.hipsY = y;
      }
    }
  }

  const tolerance = options.toleranceDegrees ?? DEFAULT_DECIMATION_DEGREES;
  const hipsYTolerance = options.hipsYTolerance ?? DEFAULT_HIPS_Y_TOLERANCE;
  const tracks: Partial<Record<CanonicalBoneId, BoneRotationTrack>> = {};
  let sampledKeyCount = 0;
  let keptKeyCount = 0;

  for (const id of CANONICAL_BONE_IDS) {
    const keys = samples.get(id);
    if (!keys || keys.length === 0) continue;
    sampledKeyCount += keys.length;
    const kept = decimateKeyframes(keys, tolerance, hipsYTolerance);
    keptKeyCount += kept.length;
    tracks[id] = { keyframes: kept };
  }

  const now = Date.now();
  return {
    hipsYRange,
    clip: {
      id: newId(),
      name: options.name,
      fps,
      durationFrames: frameCount,
      loop: options.loop ?? true,
      tracks,
      createdAt: now,
      updatedAt: now,
    },
    sampledKeyCount,
    keptKeyCount,
    warnings,
  };
}
